<template>
  <div class="zoox-filter-group zoox-component">
    <fieldset>
      <legend>{{ label }}</legend>
    </fieldset>

    <div class="wrapper">
      <div v-if="$scopedSlots['prepend-inner']" class="prepend-inner">
        <slot name="prepend-inner"> </slot>
      </div>

      <div class="content">
        <div class="chips">
          <div
            v-for="(filter, index) in filters.slice(0, maxItems)"
            :key="index"
          >
            <z-tooltip top :open-delay="500">
              <template #activator="{on, attrs}">
                <z-chip
                  v-bind="attrs"
                  x-small
                  selected
                  icon-right
                  color="primary"
                  class="zoox-filter-group-item"
                  style="cursor: pointer"
                  :icon="deleteIcon"
                  :style="{'--filter-width': filterWidth + 'px'}"
                  :text="filter.text"
                  @click:icon="removeFilter(filter)"
                  @click="$emit('click:chip', filter)"
                  v-on="on"
                ></z-chip>
              </template>

              {{ filter.text }}
            </z-tooltip>
          </div>

          <div v-if="filters.length > maxItems">
            <z-tooltip :open-delay="500" :disabled="isMenuOpened">
              <template #activator="{on, attrs}">
                <z-chip
                  id="more-chip"
                  x-small
                  selected
                  icon-right
                  icon="$more_vert_rounded"
                  style="cursor: pointer"
                  class="zoox-filter-group-item"
                  :text="'Mais ' + (filters.length - maxItems)"
                  v-bind="attrs"
                  v-on="on"
                  @change="isMenuOpened = !isMenuOpened"
                  @click="isMenuOpened = !isMenuOpened"
                >
                </z-chip>
              </template>
              Clique para ver mais
            </z-tooltip>

            <v-menu
              v-model="isMenuOpened"
              activator="#more-chip"
              content-class="mt-2"
              offset-y
              bottom
            >
              <z-card class="pa-4 elevation-1" width="auto">
                <template #content>
                  <div class="menu-items-chips">
                    <z-chip
                      v-for="(filter, index) in filters.slice(maxItems)"
                      :key="index"
                      style="cursor: pointer"
                      x-small
                      selected
                      icon-right
                      color="primary"
                      :icon="deleteIcon"
                      :text="filter.text"
                      @click:icon="removeFilter(filter)"
                      @click="$emit('click:chip', filter)"
                    />
                  </div>
                </template>
              </z-card>
            </v-menu>
          </div>
        </div>

        <div class="delete-icon">
          <z-tooltip>
            <template #activator="{on}" top>
              <z-icon
                v-ripple
                style="cursor: pointer"
                color="primary"
                size="20"
                v-on="on"
                @click="removeAllFilters"
              >
                $delete_forever_rounded
              </z-icon>
            </template>
            {{ removeAllFiltersTooltip }}
          </z-tooltip>
        </div>
      </div>

      <div v-if="$scopedSlots['append-inner']" class="append-inner">
        <slot name="append-inner"> </slot>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * @displayName Filter Group
 */
export default {
  name: 'ZFilterGroup',
  props: {
    /**
     * Array de filtros selecionados
     */
    filters: {
      type: Array,
      default: () => [],
      required: true,
    },
    /**
     * Quantidade máxima de filtros visiveis no componente
     */
    maxItems: {
      type: Number,
      default: 3,
    },
    /**
     * Label do componente
     */
    label: {
      type: String,
      default: null,
    },
    /**
     * Define o texto do tooltip no botão de remove-all
     */
    removeAllFiltersTooltip: {
      type: String,
      default: 'Remover todos os filtros',
    },
    /**
     * Define a largura em pixels do chip de filtro
     */
    filterWidth: {
      type: Number,
      default: 60,
    },
    /**
     * Define o icone de remoção dos filtros
     */
    deleteIcon: {
      type: String,
      default: '$delete',
      required: false,
    },
  },

  data() {
    return {
      isMenuOpened: false,
    }
  },

  methods: {
    removeFilter(filter) {
      /**
       * Evento emitido ao clicar no filtro
       */
      this.$emit('remove-filter', filter)
    },
    removeAllFilters() {
      /**
       * Evento emitido ao clicar no botão de remover todos os filtros
       */
      this.$emit('remove-all')
    },
  },
}
</script>

<style lang="scss">
.zoox-filter-group-item {
  .zoox-chip-text {
    width: var(--filter-width);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>

<style lang="scss" scoped>
.zoox-filter-group {
  padding: 0 12px;
  display: flex;
  align-items: stretch;
  border-radius: 15px;
  position: relative;
  width: 100%;

  fieldset {
    width: 100%;
    color: rgba(0, 0, 0, 0.38);
    padding-left: 8px;
    border: 1px solid;
    bottom: 0;
    left: 0;
    position: absolute;
    top: 0px;
    border-radius: inherit;
    border-collapse: collapse;

    legend {
      font-size: 0.775rem;
      color: rgba(0, 0, 0, 0.38);
      margin-bottom: 10px !important;
      display: table;
      border: 0;
      max-width: 100%;
      text-align: left;
      line-height: 11px;
      position: relative;
      top: -9px;
    }
  }

  .wrapper {
    display: flex;
    align-items: center;
    padding: 3px 8px;
    width: 100%;

    .content {
      display: flex;
      gap: 20px;
      align-items: baseline;
      justify-content: space-between;
      height: 100%;
      width: 100%;
      flex: 1;

      .chips {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 5px;
        position: relative;
      }

      .delete-icon {
        justify-self: flex-end;
        align-self: center;
      }
    }

    .prepend-inner {
      margin-right: 15px;
    }

    .append-inner {
      margin-left: 15px;
    }
  }
}

.menu-items-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;

  & > * {
    justify-content: center;
    flex-grow: 999;
  }
}
</style>
